import type {
  OptionAnswer,
  OptionInputFieldResponse,
  OptionItem,
  OptionResponse,
  TransactionInput,
} from '../apis/fvillage'
import { getPublication } from '../hooks/usePublication'

export const getOptionInputFieldLabel = (
  language: string,
  inputField: OptionInputFieldResponse
): string => getPublication(language, inputField.labels)?.label ?? ''

// Answers follow the order of inputFields so the form field names
// (`answers.{index}.value`) stay index-stable; stored answers are matched by
// input field id because the field list can change between visits.
export const toFormOptionAnswers = (
  inputFields: OptionInputFieldResponse[] | undefined,
  storedAnswers: OptionAnswer[] = []
): { inputField: number; value: string }[] =>
  (inputFields ?? []).map((inputField) => ({
    inputField: inputField.id,
    value:
      storedAnswers.find((answer) => answer.inputField === inputField.id)
        ?.value ?? '',
  }))

export const getOptionAnswerLines = (
  language: string,
  inputFields: OptionInputFieldResponse[],
  answers: OptionAnswer[]
): { id: number; label: string; value: string }[] =>
  inputFields.flatMap((inputField) => {
    const value = answers
      .find((answer) => answer.inputField === inputField.id)
      ?.value?.trim()
    if (!value) {
      return []
    }
    const choice = inputField.choices.find((item) => item.key === value)
    return [
      {
        id: inputField.id,
        label: getOptionInputFieldLabel(language, inputField),
        value: choice
          ? getPublication(language, choice.labels)?.label ?? value
          : value,
      },
    ]
  })

// The backend keeps the first answer set per option, so a repeated option
// (e.g. a GameTicket entry per night) is shown once.
export const getOptionAnswerGroups = (
  language: string,
  options: OptionResponse[],
  items: OptionItem[],
  input?: TransactionInput
): {
  option: number
  name: string
  lines: ReturnType<typeof getOptionAnswerLines>
}[] => {
  // An empty server array is authoritative; only older backends omit snapshots (SB-2908).
  const savedAnswers = input?.optionAnswers
  if (savedAnswers !== undefined) {
    return Array.from(new Set(savedAnswers.map(({ option }) => option))).map(
      (option) => ({
        option,
        name:
          getPublication(
            language,
            input?.options?.find((item) => item.option?.id === option)?.option
              ?.publications ?? []
          )?.name ?? '',
        lines: savedAnswers
          .filter((answer) => answer.option === option)
          .map(({ id, label, value }) => ({ id, label, value })),
      })
    )
  }

  return items
    .filter(
      (item, index) =>
        item.count &&
        items.findIndex(
          (other) => other.count && other.option === item.option
        ) === index
    )
    .flatMap((item) => {
      const option = options.find(({ id }) => id === item.option)
      const lines = getOptionAnswerLines(
        language,
        option?.inputFields ?? [],
        item.answers ?? []
      )
      if (!option || lines.length === 0) {
        return []
      }
      return [
        {
          option: option.id ?? 0,
          name: getPublication(language, option.publications ?? [])?.name ?? '',
          lines,
        },
      ]
    })
}
